import whatsappService from '../services/whatsapp.service'

// Estado inicial
const initialState = {
  instances: [],
  currentInstance: null,
  chats: [],
  currentChat: null,
  messages: [],
  loading: false,
  error: null,
  qrcode: null,
  connectionState: null
}

// Getters
const getters = {
  allInstances: state => state.instances,
  currentInstance: state => state.currentInstance,
  allChats: state => state.chats,
  currentChat: state => state.currentChat,
  chatMessages: state => state.messages,
  isLoading: state => state.loading,
  hasError: state => !!state.error,
  errorMessage: state => state.error,
  qrcode: state => state.qrcode,
  connectionState: state => state.connectionState
}

// Actions
const actions = {
  // Instâncias
  async fetchInstances({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await whatsappService.getAllInstances()
      if (response.success) {
        commit('SET_INSTANCES', response.instances)
      } else {
        commit('SET_ERROR', response.message)
      }
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao buscar instâncias')
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchInstance({ commit }, instanceId) {
    commit('SET_LOADING', true)
    try {
      const response = await whatsappService.getInstance(instanceId)
      if (response.success) {
        commit('SET_CURRENT_INSTANCE', response.instance)
      } else {
        commit('SET_ERROR', response.message)
      }
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao buscar instância')
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createInstance({ commit, dispatch }, instanceData) {
    commit('SET_LOADING', true)
    try {
      const response = await whatsappService.createInstance(instanceData)
      if (response.success) {
        commit('ADD_INSTANCE', response.instance)
        return response.instance
      } else {
        commit('SET_ERROR', response.message)
        return null
      }
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao criar instância')
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async connectInstance({ commit }, instanceId) {
    commit('SET_LOADING', true)
    try {
      const response = await whatsappService.connectInstance(instanceId)
      if (response.success && response.connection_data) {
        if (response.connection_data.qrcode) {
          commit('SET_QRCODE', response.connection_data.qrcode)
        }
        return response.connection_data
      } else {
        commit('SET_ERROR', response.message || 'Erro na resposta de conexão')
        return null
      }
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao conectar instância')
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async checkConnectionState({ commit }, instanceId) {
    commit('SET_LOADING', true)
    try {
      const response = await whatsappService.getConnectionState(instanceId)
      if (response.success && response.state) {
        commit('SET_CONNECTION_STATE', response.state)
        return response.state
      } else {
        commit('SET_ERROR', response.message || 'Erro ao verificar estado')
        return null
      }
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao verificar estado da conexão')
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async logoutInstance({ commit, dispatch }, instanceId) {
    commit('SET_LOADING', true)
    try {
      const response = await whatsappService.logoutInstance(instanceId)
      if (response.success) {
        // Atualiza a lista de instâncias após logout
        dispatch('fetchInstances')
        return true
      } else {
        commit('SET_ERROR', response.message)
        return false
      }
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao desconectar instância')
      return false
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteInstance({ commit, dispatch }, instanceId) {
    commit('SET_LOADING', true)
    try {
      const response = await whatsappService.deleteInstance(instanceId)
      if (response.success) {
        commit('REMOVE_INSTANCE', instanceId)
        return true
      } else {
        commit('SET_ERROR', response.message)
        return false
      }
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao excluir instância')
      return false
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Webhook e Settings
  async setWebhook({ commit }, { instanceId, webhookData }) {
    commit('SET_LOADING', true)
    try {
      const response = await whatsappService.setWebhook(instanceId, webhookData)
      if (!response.success) {
        commit('SET_ERROR', response.message)
      }
      return response.success
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao configurar webhook')
      return false
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async setSettings({ commit }, { instanceId, settings }) {
    commit('SET_LOADING', true)
    try {
      const response = await whatsappService.setSettings(instanceId, settings)
      if (!response.success) {
        commit('SET_ERROR', response.message)
      }
      return response.success
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao configurar settings')
      return false
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Mensagens
  async sendMessage({ commit }, { instanceId, messageData }) {
    commit('SET_LOADING', true)
    try {
      const response = await whatsappService.sendMessage(instanceId, messageData)
      if (!response.success) {
        commit('SET_ERROR', response.message)
      }
      return response.success ? response.response : null
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao enviar mensagem')
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async sendMediaMessage({ commit }, { instanceId, mediaData }) {
    commit('SET_LOADING', true)
    try {
      const response = await whatsappService.sendMediaMessage(instanceId, mediaData)
      if (!response.success) {
        commit('SET_ERROR', response.message)
      }
      return response.success ? response.response : null
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao enviar mídia')
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Chats e mensagens
  async fetchChats({ commit }, instanceId) {
    commit('SET_LOADING', true)
    try {
      const response = await whatsappService.getChats(instanceId)
      if (response.success) {
        commit('SET_CHATS', response.chats)
      } else {
        commit('SET_ERROR', response.message)
      }
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao buscar chats')
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchChatMessages({ commit }, { instanceId, chatId }) {
    commit('SET_LOADING', true)
    try {
      const response = await whatsappService.getChatMessages(instanceId, chatId)
      if (response.success) {
        commit('SET_CURRENT_CHAT', response.chat)
        commit('SET_MESSAGES', response.messages)
      } else {
        commit('SET_ERROR', response.message)
      }
    } catch (error) {
      commit('SET_ERROR', error.message || 'Erro ao buscar mensagens')
    } finally {
      commit('SET_LOADING', false)
    }
  },

  clearError({ commit }) {
    commit('SET_ERROR', null)
  }
}

// Mutations
const mutations = {
  SET_LOADING(state, status) {
    state.loading = status
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_INSTANCES(state, instances) {
    state.instances = instances
  },
  SET_CURRENT_INSTANCE(state, instance) {
    state.currentInstance = instance
  },
  ADD_INSTANCE(state, instance) {
    state.instances.push(instance)
  },
  REMOVE_INSTANCE(state, instanceId) {
    state.instances = state.instances.filter(instance => instance.id !== instanceId)
    if (state.currentInstance && state.currentInstance.id === instanceId) {
      state.currentInstance = null
    }
  },
  SET_CHATS(state, chats) {
    state.chats = chats
  },
  SET_CURRENT_CHAT(state, chat) {
    state.currentChat = chat
  },
  SET_MESSAGES(state, messages) {
    state.messages = messages
  },
  ADD_MESSAGE(state, message) {
    state.messages.unshift(message)
  },
  SET_QRCODE(state, qrcode) {
    state.qrcode = qrcode
  },
  SET_CONNECTION_STATE(state, connectionState) {
    state.connectionState = connectionState
  },
  CLEAR_QRCODE(state) {
    state.qrcode = null
  }
}

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations
} 